<template>
  <div
    id="do-application-data"
    class="flex h-full flex-col space-y-5 overflow-y-auto px-4 pt-1"
  >
    <div class="flex flex-col space-y-3">
      <lf-input
        :value="activeStep?.label || ''"
        class="h-10 w-full"
        name="block_name"
        data-cy="block-name-input"
        :placeholder="$t('ORCHESTRATION.BLOCK_NAME')"
        no-margin
        no-padding
        immediate
        :disabled="isActiveTemplatePublished"
        @key-released="handleLabelChange"
      />
      <search-input
        v-model="searchPhrase"
        data-cy="dsd-search-input"
        class="h-10 w-full"
        no-padding
        no-margin
        :delay="100"
        :disabled="isActiveTemplatePublished"
      />
    </div>
    <div class="flex flex-col space-y-2 overflow-auto rounded-sm bg-white p-2">
      <no-data-found v-if="!filteredItems.length" />
      <lf-checkbox
        v-if="filteredItems.length"
        name="attributes-select-all"
        value="select-all"
        class="flex items-center space-x-2 p-1"
        label-class="text-gray-400"
        :model-value="!!areAllFilteredSelected"
        @update:model-value="areAllFilteredSelected = $event"
      >
        {{ $t("COMMON.SELECT_ALL") }}
      </lf-checkbox>
      <lf-checkbox
        v-for="attribute in filteredItems"
        :key="attribute.id"
        class="flex items-center space-x-2 p-1 break-all"
        label-class="w-full"
        :value="attribute.id"
        :name="attribute.id"
        :disabled="isActiveTemplatePublished"
        :class="{ 'hover:bg-gray-100': !isActiveTemplatePublished }"
        :model-value="selectedAttributesIds"
        @update:model-value="handleSave"
      >
        <highlight-text :content="attribute.name" :needle="searchPhrase" />
      </lf-checkbox>
    </div>
  </div>
  <webhook-configuration
    v-if="activeStep?.id"
    :webhooks="activeStep.webhooks"
    :step-id="activeStep.id"
  />
  <modal-buttons
    v-if="!isActiveTemplatePublished"
    :on-delete="handleDelete"
    class="bg-disabled p-6"
    :white-background="false"
  />
</template>

<script setup lang="ts">
import { useFuse } from "@vueuse/integrations/useFuse";
import { computed, ref } from "vue";
import { useStore } from "vuex";

import { FUZZY_SEARCH_THRESHOLD_DEFAULT } from "@/helpers/constants";
import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import HighlightText from "@/components/HighlightText.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import WebhookConfiguration from "@/views/profile/components/dataOrchestration/WebhookConfiguration.vue";
import NoDataFound from "@/views/profile/components/dataOrchestration/configPanel/NoDataFound.vue";

import type { IAttribute, IStep } from "@/models/orchestration";

const { getters, commit } = useStore();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();
const searchPhrase = ref("");

const activeStep = computed<IStep | null>(
  () => getters["orchestration/activeStep"]
);
const attributes = computed<IAttribute[]>(
  () => getters["orchestration/appDataAttributes"]
);

const { results } = useFuse(searchPhrase, attributes, {
  fuseOptions: {
    keys: ["name"],
    threshold: FUZZY_SEARCH_THRESHOLD_DEFAULT,
  },
  matchAllWhenSearchEmpty: true,
});
const filteredItems = computed(() => results.value.map(({ item }) => item));

const areAllFilteredSelected = computed({
  get() {
    return (
      !!filteredItems.value.length &&
      filteredItems.value.every((attribute) =>
        selectedAttributesIds.value.includes(attribute.id)
      )
    );
  },
  set(value) {
    const currentSelection = [...selectedAttributesIds.value];
    const filteredIds = filteredItems.value.map((attribute) => attribute.id);

    if (value) {
      // Add all filtered items that aren't already selected
      filteredIds.forEach((id) => {
        if (!currentSelection.includes(id)) {
          currentSelection.push(id);
        }
      });
    } else {
      // Remove all filtered items from selection
      filteredIds.forEach((id) => {
        const index = currentSelection.indexOf(id);
        if (index > -1) {
          currentSelection.splice(index, 1);
        }
      });
    }

    handleSave(currentSelection);
  },
});

const selectedAttributes = computed(() => activeStep.value?.attributes || []);

const selectedAttributesIds = computed(() =>
  selectedAttributes.value.map((attr) => attr.id)
);

const handleSave = (attributes: IAttribute["id"][] = []) => {
  const removedAttributes = selectedAttributes.value?.filter(
    (attribute) => !attributes.includes(attribute.id)
  );
  commit("orchestration/setStepAttributes", {
    stepId: activeStep.value?.id,
    attributes: attributes.map((id) => ({ id })),
  });
  if (removedAttributes.length) {
    commit("orchestration/removeAttributesFromOutcomes", {
      stepId: activeStep.value?.id,
      removedAttributes,
    });
  }
};

const handleLabelChange = (value: string) => {
  commit("orchestration/setStepLabel", {
    stepId: activeStep.value?.id,
    label: value,
  });
};

const handleDelete = () => {
  commit("orchestration/deleteStep", { stepId: activeStep.value?.id });
  commit("orchestration/setActiveConfigPanel", "");
  commit("orchestration/setActiveStep", {});
};
</script>
